import { Fragment } from 'react';

import { CREHANA_BASE_URL_SEO_PRD } from '@crehana/constants';
import type { CountryPrefix } from '@crehana/ts-types';
import MainFooter from '@crehana/web/MainFooter';
import { NextSeo } from 'next-seo';

import MainMenu from '@/shared/components/MainMenu';
import { initializeApollo } from '@/shared/libs/apollo';
import { getI18nData } from '@/shared/utils/getI18n/getI18n';
import type { CustomGetServerSideProps } from '@/shared/utils/getServerSideProps';
import type { AppProps } from '@/shared/utils/types';

import {
  PRECAMPAIGN_AMPLITUDE_PAGE_NAME,
  PRECAMPAIGN_PAGE_KEY,
  SEO,
} from './constants';
import CAMPAIGN_DATA from './graphql/CampaignData.graphql';
import type {
  CampaignLandingData,
  CampaignLandingDataVariables,
} from './graphql/types/CampaignLandingData';
import localeEn from './i18n/localeEn';
import localeEs from './i18n/localeEs';
import localePt from './i18n/localePt';
import Campaign from './pages/Campaign';
import PreCampaign from './pages/PreCampaign';
import type { CampaignProps, PageProps, PreCampaignProps } from './types';

const App: React.FC<React.PropsWithChildren<AppProps<PageProps>>> = ({
  countryPrefix,
  data,
}) => {
  const shouldRenderCampaignLanding = data.hasBegun && !data.hasFinished;

  const urlWithPrefix = `${CREHANA_BASE_URL_SEO_PRD}/${countryPrefix}/eventos/${data.slug}/`;

  return (
    <Fragment>
      <NextSeo
        title={data.seo?.metaTitle}
        description={data.seo?.metaDescription}
        canonical={urlWithPrefix}
        twitter={{
          cardType: 'summary',
          site: '@crehana',
          handle: '@crehana',
        }}
        openGraph={{
          url: urlWithPrefix,
          title: data.seo?.metaTitle,
          description: data.seo?.metaDescription,
          images: [{ url: SEO.og_data.image.url, alt: SEO.og_data.image.alt }],
          type: SEO.og_data.type,
          site_name: SEO.og_data.site_name,
        }}
        additionalMetaTags={[{ property: 'lang', content: 'es' }]}
      />
      <div className="bg-base-dark">
        <MainMenu
          isTransparent
          position="absolute"
          theme={{
            nav: 'dark',
            submenu: 'dark',
            searchDialog: 'dark',
            searchInput: 'dark',
            searchOverlay: 'dark',
          }}
          countryPrefix={countryPrefix || ''}
          amplitudePageName={PRECAMPAIGN_AMPLITUDE_PAGE_NAME}
          layoutConfig={{
            hideResourcesDropdown: true,
          }}
        />

        {shouldRenderCampaignLanding ? (
          <Campaign {...(data as CampaignProps)} />
        ) : (
          <PreCampaign {...data} />
        )}
        <MainFooter isDark />
      </div>
    </Fragment>
  );
};

export const getServerSideProps: CustomGetServerSideProps<PageProps> = async ({
  params,
  req,
  query,
}) => {
  let slug = params?.slug;

  slug = Array.isArray(slug) ? slug[0] : slug;

  if (!slug) {
    return {
      notFound: true,
    };
  }

  const countryPrefix = (query.country as CountryPrefix) || '';

  // Hot fix on campaigns days... i don't think this is gonna works but it's been required by backend team
  req.headers['creh-country'] = countryPrefix;

  const apolloClient = initializeApollo({
    countryPrefix: countryPrefix || '',
    pageKey: PRECAMPAIGN_PAGE_KEY,
  });

  const [campaignData, i18nData] = await Promise.all([
    apolloClient.query<CampaignLandingData, CampaignLandingDataVariables>({
      query: CAMPAIGN_DATA,
      variables: {
        slug,
      },
    }),
    getI18nData({
      apolloClient,
      headers: req.headers,
      pageKey: PRECAMPAIGN_PAGE_KEY,
      defaultTranslations: {
        es: localeEs.es,
        pt: localePt.pt,
        en: localeEn.en,
      },
    }),
  ]);

  if (!campaignData.data.campaignLanding?.id) {
    return {
      notFound: true,
    };
  }

  let dataProps: PreCampaignProps | CampaignProps = {};

  if (
    campaignData.data.campaignLanding?.hasBegun &&
    !campaignData.data.campaignLanding?.hasFinished
  ) {
    dataProps = campaignData.data.campaignLanding as CampaignProps;
  } else {
    dataProps = campaignData.data.campaignLanding as PreCampaignProps;
  }

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      pageKey: PRECAMPAIGN_PAGE_KEY,
      ...i18nData,
      countryPrefix,
      data: dataProps,
    },
  };
};

export default App;
